/**
 * GET /v1/feedback-board — the school's Linear-backed feedback board.
 *
 * Admin-gated, school-scoped (requireSchoolAdmin — KTD7). Read live from
 * Linear's GraphQL API, no DB mirror (KTD1). The school's Linear customer
 * resolves by `externalIds == School.publicId` (KTD2); when no customer
 * matches, the backend returns an explicit empty board (200, not an error — R2).
 *
 * The board splits into two surfaces (2026-07-08 restructure):
 * - `columns` — the three kanban columns, always returned in canonical order
 *   (planned → in_progress → done) so the web client renders a stable layout
 *   even when a column is empty.
 * - `inbox` — a flat list of Triage-state cards, rendered on its own Inbox
 *   page as a data table (NOT a kanban column).
 *
 * See: docs/plans/2026-07-07-002-feature-support-board-intake-states-plan.md
 *      docs/_archive/2026-10-17-plans-sweep/plans/2026-06-18-002-feat-customer-feedback-roadmap-plan.md (U2/U3 origin)
 */

import { z } from 'zod'
import { FeedbackCardSchema, FeedbackColumnSchema } from '../../schemas/entities/feedback-board'

// ============================================================================
// GET /v1/feedback-board
// ============================================================================

/**
 * Board response. `columns` is the three kanban columns in order (planned,
 * in_progress, done); `inbox` is the separate flat list of Triage-state cards.
 * `mapped` is false when the school has no resolved Linear customer (empty-state
 * signal for the web client — R2).
 */
export const FeedbackBoardResponseSchema = z.object({
  /**
   * Whether the school resolved to a Linear customer. False → board is empty
   * because the school's customer carries no matching `externalId` yet (R2),
   * NOT because the customer has no needs.
   */
  mapped: z.boolean(),
  /** The three kanban columns (planned, in_progress, done) in display order. */
  columns: z.array(FeedbackColumnSchema),
  /**
   * Triage-state cards (Linear `triage`). A flat list surfaced on the standalone
   * Inbox page (data table), separate from the kanban `columns`. Empty when the
   * school has no untriaged requests (and always `[]` for an unmapped board).
   */
  inbox: z.array(FeedbackCardSchema).default([])
})
export type FeedbackBoardResponse = z.infer<typeof FeedbackBoardResponseSchema>
